import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { GcCountdownPill } from '~/components/common/GcCountdownPill'
import { now as serverNow } from '~/models/clock'
import { formatGcRemainingTime } from '~/models/gcRemainingTime'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { computeCountdown } from '~/pages/IntradayBidding/models/gcCountdown'

type Props = {
  /** カウントダウン対象の GC 時刻。 undefined のとき「GC済」 を表示。 */
  gcAt: Date | undefined
  /**
   * カウントダウン対象のコマ番号 (1-48)。 全コマ GC 済 (= 過去日 / 当日 22:30 以降)
   * では対象コマが無いので undefined (このとき `gcAt` も undefined になる)。
   */
  targetKoma: number | undefined
  /**
   * 受付前 (= 選択中の受渡日が受渡日前日 12:00 JST より前、 AC-BIN-089c) の
   * 受付開始時刻。 指定時は GC ではなく**受付開始までの残時間**を表示する
   * (AC-BIN-001)。 undefined = 受付開始済 (= 通常の GC カウントダウン)。
   *
   * 受付前は札を作れないので、 次の GC を数えてもユーザーの操作に結び付かない。
   * 対象コマ (`.gc-slot`) も出さない (受付開始は受渡日全体に掛かる制約で、
   * 特定コマの締切ではないため)。
   */
  acceptanceOpenAt?: Date
}

/** 残り 10 分以内で強調表示に切り替える閾値 (design `.gc-remaining.gc-urgent`)。 */
const URGENT_THRESHOLD_SECONDS = 10 * 60

/**
 * GC カウントダウン display。 1 秒粒度で再描画 (= setInterval 内で now state を更新)。
 *
 * 見た目は全画面共通の [[GcCountdownPill]] (design `.gc-countdown`、 2026-10-05 統合)。
 * 「GC まで」 残り時間 | 「次の GC」 コマ・時間帯 の順。 本 component は
 * 何を数えるか (次の GC / 受付開始) と残り時間の計算だけを持つ。
 *
 * 経緯: #4858 で対象コマと残時間を 1 つのバッジに集約した。 旧実装は「`GC まで` + 残時間」 のバッジと
 * 「`対象コマ: N`」 の素テキストが呼び出し元で横並びの 2 要素構成で、 同じ GC を指す
 * 2 情報が視覚的に無関係に見えていた (issue #4858、 デザイナー裁定 2026-08-14 で
 * 情報量の多い需要想定側の集約バッジに寄せる方針が確定)。
 *
 * 需要想定の双子 (`~/pages/DemandForecastDaily/components/GcCountdown`) とは
 * 見た目・文言・残時間の桁を揃えるが、 component は統合しない — GC の選び方が
 * 業務上異なる (本画面は境界ごとに次の GC を取り直す / 需要想定は固定コマを狙い撃つ)。
 */
export const GcCountdown = ({
  gcAt,
  targetKoma,
  acceptanceOpenAt,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [now, setNow] = useState(serverNow())

  useEffect(() => {
    const tick = setInterval(() => {
      setNow(serverNow())
    }, 1000)
    return () => {
      clearInterval(tick)
    }
  }, [])

  // 受付前は受付開始、 それ以外は次の GC が対象。 バッジの形 (ラベル / 対象コマ /
  // 残時間) は同じで、 何を数えるかだけが変わる。
  const isAcceptanceCountdown = acceptanceOpenAt !== undefined
  const targetAt = acceptanceOpenAt ?? gcAt
  const parts =
    targetAt === undefined ? undefined : computeCountdown(now, targetAt)
  // 「GC済」 (終了表示) にする条件。 2 経路ある:
  //   - 全コマ GC 済 (受渡日が過去 / 当日 22:30 以降) → gcAt も targetKoma も undefined
  //   - 描画中に対象 GC が到来 → targetKoma は残っている
  // どちらも終了表示 (中立色 + 状態の文言だけ) にする。 2026-10-05 の統合で、 終了時は
  // ラベル・区切り・コマを隠して文言だけを出す形に揃えた (design `.gc-countdown--passed`)。
  // 旧形は後者でコマ表記を残していた。
  const isPast = parts === undefined || parts.isPast
  const isUrgent =
    parts !== undefined &&
    !parts.isPast &&
    parts.totalSeconds < URGENT_THRESHOLD_SECONDS

  return (
    <GcCountdownPill
      passedText={
        isPast
          ? t(
              isAcceptanceCountdown
                ? 'page:biddingIntraday.gcCountdown.acceptanceStarted'
                : 'page:biddingIntraday.gcCountdown.afterGc',
            )
          : undefined
      }
      remaining={
        parts === undefined ? '' : formatGcRemainingTime(parts.totalSeconds)
      }
      // 受付前は受付開始までを数える。 受付開始は受渡日全体に掛かる制約なので
      // 対象コマ (と「次の GC」) は出さない (AC-BIN-001)。
      remainingLabel={
        isAcceptanceCountdown
          ? t('page:biddingIntraday.gcCountdown.acceptanceLabel')
          : undefined
      }
      slot={
        !isAcceptanceCountdown && targetKoma !== undefined
          ? {
              koma: targetKoma,
              range: komaNumberTimeRange(
                targetKoma,
                t('common:gcCountdown.rangeSeparator'),
              ),
            }
          : undefined
      }
      urgent={isUrgent}
    />
  )
}
